import { memo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, Menu, Settings, LogOut, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/contexts/AuthContext';
import { useUIStore } from '@/stores/assessmentStore';
import { NotificationPanel } from '@/components/NotificationPanel';
import { ROUTES } from '@/constants/routes';

// Memoized header component to prevent unnecessary re-renders
function HeaderComponent() {
  const { user, signOut } = useAuth();
  const { toggleSidebar } = useUIStore();
  const [searchFocused, setSearchFocused] = useState(false);
  const navigate = useNavigate();
  
  const getDisplayName = () => {
    const metadata = user?.user_metadata as Record<string, string> | undefined;
    if (metadata?.first_name && metadata?.last_name) {
      return `${metadata.first_name} ${metadata.last_name}`;
    }
    return user?.email?.split('@')[0] || 'Teacher Name';
  };

  const getInitials = () => {
    const metadata = user?.user_metadata as Record<string, string> | undefined;
    if (metadata?.first_name && metadata?.last_name) {
      return `${metadata.first_name[0]}${metadata.last_name[0]}`.toUpperCase();
    }
    return user?.email?.charAt(0).toUpperCase() || 'U';
  };

  const handleLogout = async () => {
    await signOut();
    navigate(ROUTES.LOGIN);
  };

  return (
    <header className="h-16 glass-header flex items-center justify-between px-4 lg:px-6 sticky top-0 z-30">
      <div className="flex items-center gap-4 flex-1">
        {/* Mobile Menu Toggle */}
        <Button
          variant="ghost"
          size="icon"
          className="lg:hidden"
          onClick={toggleSidebar}
          aria-label="Toggle navigation menu"
        >
          <Menu className="h-5 w-5" />
        </Button>
        
        {/* Search */}
        <div className={`relative w-full max-w-md transition-all duration-200 ${searchFocused ? 'max-w-lg' : ''}`}>
          <Search 
            className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 transition-colors ${searchFocused ? 'text-primary' : 'text-muted-foreground'}`} 
            aria-hidden="true" 
          />
          <Input
            type="search"
            placeholder="Search children, assessments..."
            className="pl-10 bg-muted/50 border-border/50 focus:bg-background input-focus-ring"
            aria-label="Search children and assessments"
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
          />
        </div>
      </div>
      
      <div className="flex items-center gap-2 sm:gap-4">
        {/* Notifications */}
        <NotificationPanel />
        
        {/* User Profile Dropdown */}
        <div className="flex items-center gap-3 pl-2 sm:pl-4 border-l border-border">
          <div className="text-right hidden sm:block">
            <p className="text-sm font-semibold text-foreground leading-tight">{getDisplayName()}</p>
            <p className="text-xs text-muted-foreground">Developmental Professional</p>
          </div>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center text-white font-semibold text-sm shadow-sm cursor-pointer hover:shadow-md transition-all hover:scale-105 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                aria-label="User menu"
              >
                {getInitials()}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-medium leading-none">{getDisplayName()}</p>
                  <p className="text-xs leading-none text-muted-foreground">
                    {user?.email}
                  </p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to={ROUTES.SETTINGS} className="cursor-pointer">
                  <User className="mr-2 h-4 w-4" />
                  <span>Profile</span>
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to={ROUTES.SETTINGS} className="cursor-pointer">
                  <Settings className="mr-2 h-4 w-4" />
                  <span>Settings</span>
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                onClick={handleLogout}
                className="cursor-pointer text-destructive focus:text-destructive"
              >
                <LogOut className="mr-2 h-4 w-4" />
                <span>Sign out</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}

export const Header = memo(HeaderComponent);
